/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/typedef */

import { Inject, Injectable } from '@angular/core';
import { StateService, UIRouter } from '@uirouter/core';
import { HttpErrorResponse, HttpRequest, HttpResponse} from '@angular/common/http';
import { CimaCore, CimaToken, SessionStorage, Util, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { Logger } from 'store/logger/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN, LoggerEvent, LoggerOptions, StorageToken } from 'core/constants';
import { LineCollection, LineDetail, LineDevice } from 'store/line/models';
import { User } from 'store/user/models';

@Injectable({
    providedIn: 'root'
})
export class LoggerService {
    private accountRole: string = '';
    private xmStore: XmStore;
    private config: IXMOptions;
    private uiRouter: UIRouter;
    private cimaToken: CimaToken;
    private state: StateService;
    private session: SessionStorage;
    private cimaCore: CimaCore;

    constructor(@Inject(CONFIG_TOKEN) config: IXMOptions, xmStore: XmStore, uiRouter: UIRouter, cimaToken: CimaToken, state: StateService, session: SessionStorage, cimaCore: CimaCore) {
        Object.assign(this, { config, xmStore, uiRouter, cimaToken, state, session, cimaCore });
        this.getUUID();
    }

    /****************************
     * Service level log *
     ****************************/
    public info(httpResponse: HttpResponse<object>, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn || this.isUnAuthApiCall(httpRequest?.url)) {
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.MEASURE,
                startTime: startTime,
                duration: endTime - startTime,
                event: LoggerEvent.MEASURE,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: httpRequest.body && JSON.stringify(Util.maskData(httpRequest.body))
                    },
                    response: {
                        status: httpResponse.status,
                        ...(!(httpRequest?.urlWithParams?.includes('/nutrition-labels')) && {
                            text: this.handleResponseBody(httpResponse)
                        })
                    },
                    otherData: this.deviceInfo(httpRequest?.url),
                    url: httpRequest.urlWithParams,
                    ...(this.isActivationURL(httpRequest.url, httpResponse) && {
                        error: {
                            code: this.getActivationErrorCode(httpRequest.url, httpResponse)
                        }
                    })
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                uri: window.location.href,
                custGuid: this.getCustGuid(),
                role: this.getAccountRole(httpRequest?.url, httpResponse),
                customerType: this.getCustomerType(),
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language,
                ...(this.session.get(StorageToken.CIMA_USER_ACCOUNT_GUID) && { cimaUserAccGuid: this.session.get(StorageToken.CIMA_USER_ACCOUNT_GUID) }),
                ...(this.session.get(StorageToken.X_TENANT_ID) && { x_tenant_id: this.session.get(StorageToken.X_TENANT_ID) }),
                ...(httpRequest?.url.includes('wearables/eligibility/requests') && { mode: this.getMode(httpResponse) }),
                ...(this.checkForDenialReasonAPI(httpRequest.url) && { denialReason: this.getDenialReason(httpRequest.url, httpResponse) }),
                ...(httpRequest?.url.includes('wearables/eligibility/requests') && { conditionalReason: this.getConditionalReason(httpResponse) }),
                ...(httpRequest?.url.includes('devices/eligibility/status') && { supportedSimType: this.getSupportedSimType(httpResponse), deviceLockStatus: this.getDeviceLockStatus(httpResponse) }),
                ...(httpRequest?.url.includes('/e911/address') && { mdn: this.getMdn(httpResponse, httpRequest) })
            }];
            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
        
    }

    /****************************
     * Content level log *
     ****************************/
    public cmsInfo(httpResponse: HttpResponse<object>, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn){
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                event: LoggerEvent.OTHERS,
                entryType: LoggerEvent.MEASURE,
                startTime: startTime,
                duration: endTime - startTime,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: JSON.stringify(httpRequest.body)
                    },
                    response: {
                        status: httpResponse.status,
                        text: JSON.stringify(httpResponse.body)
                    },
                    url: httpRequest.urlWithParams
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                uri: window.location.href,
                custGuid: this.getCustGuid(),
                customerType: this.getCustomerType(),
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Page level log *
     ****************************/
    public pageInfo(pageInfo: string): void {
        if (this.cimaToken.isLoggedIn || this.isGeminiActivation()){
            const startTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                event: LoggerEvent.OTHERS,
                entryType: LoggerEvent.OTHERS,
                startTime: startTime,
                duration: 0,
                data: {
                    message: pageInfo
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                custGuid: this.getCustGuid(),
                role: this.accountRole,
                customerType: this.getCustomerType(),
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * QuickPay Survey log *
     ****************************/
    public quickPaySurveyInfo(surveyResponse: string, authGuid: string): void {
        if (authGuid){
            const startTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                event: LoggerEvent.OTHERS,
                entryType: LoggerEvent.SURVEY,
                startTime: startTime,
                duration: 0,
                surveyResponse: surveyResponse,
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                authGuid: authGuid,
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Service error level log *
     ****************************/
    public error(httpError: HttpErrorResponse, httpRequest: HttpRequest<object>, startTime: number): void {
        if (this.cimaToken.isLoggedIn || this.isUnAuthApiCall(httpRequest?.url)){
            const errorMessage = this.isNowMobile() ? httpError?.error?.error?.errorMessage : httpError?.error?.message;
            const errorCode = this.isNowMobile() ? httpError?.error?.error?.errorCode : httpError?.error?.code;
            const endTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.ERROR,
                startTime: startTime,
                duration: endTime - startTime,
                event: LoggerEvent.MEASURE,
                data: {
                    request: {
                        method: httpRequest.method,
                        data: httpRequest.body && JSON.stringify(httpRequest.body)
                    },
                    response: {
                        status: httpError.status,
                        text: httpError.message
                    },
                    otherData: this.deviceInfo(httpRequest?.url),
                    error: {
                        name: httpError.name,
                        message: errorMessage,
                        code: errorCode
                    },
                    url: httpRequest.urlWithParams
                },
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                custGuid: this.getCustGuid(),
                role: this.accountRole,
                customerType: this.getCustomerType(),
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language
            }];
            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    /****************************
     * Script error level log *
     ****************************/
    public scriptError(httpError: HttpErrorResponse): void {            
        if (this.cimaToken.isLoggedIn){
            const startTime: number = performance.now();
            const logData: object[] = [{
                appname: this.getAppName(),
                entryType: LoggerEvent.ERROR,
                startTime: startTime,
                duration: 0,
                event: LoggerEvent.ERROR,
                errorName: httpError?.name,
                errorMessage: httpError?.message?.replace(/'/g, ''),
                _time: Date.now(),
                component: this.xmComponent(),
                env: this.config.LOG_ENV,
                page: this.pageName(),
                uuid: this.session.get(StorageToken.UUID),
                custGuid: this.getCustGuid(),
                customerType: this.getCustomerType(),
                uri: window.location.href,
                role: this.accountRole,
                pageLanguage: window.lzGetCurrentLanguage ? window.lzGetCurrentLanguage() : window.navigator.language
            }];

            XmStoreUtil.defaultCatch(this.xmStore.persist<Logger>(StoreAction.POST_LOGGER, { payLoad: logData }));
        }
    }

    private getUUID(): void {
        if (!this.session.get(StorageToken.UUID)) {
            this.session.set(StorageToken.UUID, Util.generateUuid());
        }
    }

    private xmComponent(): string {
        const name: string = this.defaultComponent();

        if (this.uiRouter.globals.transition) {
            return this.uiRouter.globals.transition.to().name;
        } else if (this.uiRouter.globals.current) {
            return this.uiRouter.globals.current.name;            
        } else {
            return this.isNowMobile() ? name : `xm-${name}`;
        }
    }

    private pageName(): string {
        if (this.uiRouter.globals.transition) {
            return this.uiRouter.globals.transition.to()?.data ? this.uiRouter.globals.transition.to()?.data?.screenName  :  this.uiRouter.globals.transition.to().name;
        } else if (this.uiRouter.globals.current) {
            return this.uiRouter.globals.current?.data ? this.uiRouter.globals.current?.data?.screenName : this.uiRouter.globals.current?.name;
        } else {
            return this.defaultComponent();
        }
    }

    private getCustGuid(): string {
        return this.cimaToken.isLoggedIn ? this.cimaToken.decodedIdToken.sub : undefined; 
    }

    private getActivationErrorCode(url: string, response: HttpResponse<any>): string {
        if (url?.includes('activation') && response.body) {
            return response.body.errorCode;
        } else {
            return undefined;
        }
    }

    private deviceInfo(url: string): string {
        if (this.state.params.line_id && this.state.params.device_id  && (url?.includes('activate') || url?.includes('activation'))) {
            let device: LineDevice;
            let line: LineDetail;
            XmStoreUtil.subscribe(this.xmStore.peek<LineCollection>(LineCollection), (collection: LineCollection) => {
                if (collection) {
                    line = collection.getLineByKey(this.state.params.line_id);
                    device = line?.getDeviceByKey(this.state.params.device_id);
                }
            });

            return JSON.stringify({ 
                simType: device?.simType,
                os: device?.productDetails?.os,
                brand: device?.productDetails?.brand,
                deviceType: device?.deviceType
            });
        }
    }

    private getAppName(): string {
        return this.isNowMobile() ? LoggerOptions.NOW_APP_NAME : LoggerOptions.APP_NAME; 
    }

    private defaultComponent(): string {
        return this.isNowMobile() ? LoggerOptions.NOW_COMPONENT : LoggerOptions.COMPONENT;
    }

    private isNowMobile(): boolean {
        return window.location.href.includes('/now');
    }

    private isGeminiActivation(): boolean {
        return window.location.href.includes('/geminiActivation');
    }

    private isActivationURL(url: string, response: HttpResponse<any>): boolean {
        return url.includes('activation') && response.body.errorCode;
    }

    private isUnAuthApiCall(url: string): boolean {
        return url?.includes('/mdn/lookup') || url?.includes('/account/quickpayment') || url?.includes('/e911/address');
    }

    private getMode(response: HttpResponse<any>): string {
        return response.body.mode;
    }

    private getDenialReason(url: string, response: HttpResponse<any>): string {
        if (url.includes('wearables/eligibility/requests')) {
            const denialReason = response.body?.eligibility?.denialReason || [];

            return denialReason.map((reason: any) => ({
                reasonCode: reason.reasonCode || '',
                reasonDescription: reason.reasonDescription || ''
            }));
        } else if (url.includes('devices/eligibility/status')) {
            return response.body?.denialReasons?.length > 0 ? response.body?.denialReasons[0]?.code : undefined;
        }
    }

    private getConditionalReason(response: HttpResponse<any>): string {
        const conditionalReason = response.body?.eligibility?.conditionalReason || [];

        return conditionalReason.map((reason: any) => ({
            reasonCode: reason.reasonCode || '',
            reasonDescription: reason.reasonDescription || ''
        }));
    }

    private getSupportedSimType(response: HttpResponse<any>): string {
        return response.body?.supportedSimType ? response.body.supportedSimType : undefined;
    }

    private getDeviceLockStatus(response: HttpResponse<any>): string {
        return response.body?.deviceLockStatus ? response.body.deviceLockStatus : undefined;
    }

    private getMdn(response: HttpResponse<any>, httpRequest: HttpRequest<any>): string {
        return response?.body?.mdn ? response.body.mdn : httpRequest?.body?.mdn ? httpRequest.body.mdn : undefined;
    }

    private checkForDenialReasonAPI(url: string): boolean {
        return url.includes('wearables/eligibility/requests') || url.includes('devices/eligibility/status');
    }

    private handleResponseBody(response: HttpResponse<object>): string {
        const body = <{ [key: string]: any }>response.body;
        if (body && typeof body === 'object' && 'message' in body && response.url?.includes('/statement/pdf')) {
            const message = body.message;
            let size = 0;
            if (typeof message === 'string') {
                size = new Blob([message]).size;
            } else if (message !== undefined) {
                size = new Blob([JSON.stringify(message)]).size;
            }
            // Convert bytes to KB with 1 decimal place
            const sizeKB = (size / 1024).toFixed(1);

            return `Text content - ${sizeKB}KB`;
        } else {
            return response.body && JSON.stringify(Util.maskData(response.body));
        }
    }

    private getAccountRole(url: string, response: HttpResponse<any>): string {
        if (url.includes('/xm/ordertech/account')) {
            this.accountRole = response?.body?.role || '';
        }

        return this.accountRole;
    }

    private getCustomerType(): string {
        // Check if ORION based on URL parameter or session storage
        if (this.session.get(StorageToken.X_TENANT_ID) === 'ORION' || 
            window.location.href.includes('os=') || 
            window.location.search.includes('os=')) {
            return 'ORION';
        }

        // Check if ORION based on account data in store
        try {
            XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
                if (user?.account?.isOrion) {
                    return 'ORION';
                }
            });
        } catch (error) {
            // If there's an error accessing the store, continue with other logic
            void error;
        }

        // Use CimaCore's isBusinessUser method to determine customer type
        // This checks the CIMA ID token's user_type field
        if (this.cimaCore.isBusinessUser || this.isGeminiActivation()) {
            return 'CBM';
        } else {
            return 'RESI';
        }
    }
}
